// WHY [BOL-ORB-DESKTOP-IDLE-PRESENCE-011]: preserve the deployed SeenStage
// desktop's 1.72 rest body. The Ops 1.0 shrink made the 161px stage a nearly
// invisible 22px speck; output still owns the much larger 3.25 crest.
export const OPS_ORB_IDLE_RADIUS_SCALE = 1.72;
export const OPS_ORB_PEAK_RADIUS_SCALE = 3.25;

const OPS_ORB_AOEDE_NOISE_FLOOR = 0.02;
const OPS_ORB_AOEDE_CREST = 0.9;
const OPS_ORB_ENVELOPE_EXPONENT = 1.7;

function clampUnit(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(1, value));
}

/**
 * Maps the existing RMS/peak analyser level to the one visual speech envelope.
 *
 * WHY [BOL-ORB-AOEDE-CONVEX-001]: seven shipped Aoede cues measure p50=.154,
 * p90=.589 and p95=.793. The imported level^.68 curve is concave: it inflates
 * quiet audio and compresses word crests, producing the reported constant-size
 * red mass. Gate the measured silence floor, then use one convex curve for body,
 * ripple, particle size and light. Falsifier: a p50 window grows materially, a
 * p90 crest stays below 44%, or any consumer substitutes raw/concave amplitude.
 */
export function opsOrbAudioEnvelope(level: number): number {
  const normalized = clampUnit(
    (clampUnit(level) - OPS_ORB_AOEDE_NOISE_FLOOR)
      / (OPS_ORB_AOEDE_CREST - OPS_ORB_AOEDE_NOISE_FLOOR)
  );
  return Math.pow(normalized, OPS_ORB_ENVELOPE_EXPONENT);
}

export function opsOrbRadiusScale(level: number): number {
  return OPS_ORB_IDLE_RADIUS_SCALE
    + opsOrbAudioEnvelope(level)
      * (OPS_ORB_PEAK_RADIUS_SCALE - OPS_ORB_IDLE_RADIUS_SCALE);
}

export function nextOpsOrbOutputVisualLevel(input: {
  currentLevel: number;
  measuredOutputLevel: number;
  outputConnected: boolean;
  elapsedSeconds: number;
}): number {
  const current = clampUnit(input.currentLevel);
  const target = input.outputConnected
    ? clampUnit(input.measuredOutputLevel)
    : 0;
  const elapsedSeconds = Math.max(0, input.elapsedSeconds);
  // WHY [BOL-ORB-OUTPUT-CROSSFADE-013]: an interruption stops Aoede
  // immediately, but snapping its last output-owned visual sample to idle made
  // barge-in/out look broken. Keep a fast waveform attack, a short word-gap
  // release, and a bounded 90 ms disconnected release. Only the measured Ops
  // output graph can raise this value; microphone/input has no parameter or
  // fallback. Falsifier: input sound pumps Luna, peaks visibly lag, or the Orb
  // jumps/carries speech motion beyond the short post-output crossfade.
  const timeConstantSeconds = target > current
    ? 0.024
    : input.outputConnected
      ? 0.045
      : 0.09;
  const coefficient = 1 - Math.exp(-elapsedSeconds / timeConstantSeconds);
  const next = current + (target - current) * coefficient;
  return next < 0.001 ? 0 : clampUnit(next);
}
